import React, { useEffect, useRef, useState } from 'react';
import { FormControl, Button, Grid, FormHelperText, Card, Box, Typography, Checkbox, FormGroup, FormControlLabel, TextField, MenuItem, ToggleButton, ToggleButtonGroup, Divider } from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import DrawIcon from '@mui/icons-material/Draw';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { postPatientAcknowledgementRequest, putPatientAcknowledgementRequest } from '../../../../redux/modules/admin/patientAcknowledgement/slice';
import { getPatientConsentRequest, postPatientConsentRequest, uploadPatientConsentRequest, resetPostPatientConsent, resetUploadPatientConsent } from '../../../../redux/modules/admin/patientConsent/slice';
import { getPatientDataRequest } from '../../../../redux/modules/admin/patientProfile/slice';
import { apiBuildUrl } from '../../../../api/client';
import ConsentSignaturePad from './consentSignaturePad';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
const validationSchema = yup.object().shape({
    acknowledgement: yup.array().transform((value, originalValue) => {
        return originalValue ? originalValue : [];
    }).min(1, 'Select at least one checkbox'),
});

// Text of the General Consent to Treatment (DWMS-FRM-007 v3). Shown to the
// patient before signing; the server embeds the same wording in the
// generated PDF (server/helper/consentPdfGenerator.js).
const CONSENT_CLAUSES = [
    'I am asking for medical care and treatment by doctor and agree to accept services which may diagnose a '
    + 'medical condition, procedures to treat my condition and routine medicals including vaccination and '
    + 'COVID-19 testing. I understand that these services will be provided to me by physicians, nurses, '
    + 'physician assistants, nurses aid and other health care providers.',
    'I understand that my agreement to accept these services will remain in effect unless I say that I no longer '
    + 'want these services or until my treatment is completed. All company medicals will be sent to the company '
    + 'requesting such medicals.',
];
const CONSENT_CLOSING =
    'I understand that my agreement to accept these services is called a General Consent and that it includes '
    + 'any routine, procedure(s) or treatment(s) such as blood drawing, physical examination, administration of '
    + 'medication(s), use of local anesthesia and other non-invasive procedures.';

const RELATIONSHIP_OPTIONS = [
    { value: 'patient', label: 'Patient (self)' },
    { value: 'parent_guardian', label: 'Parent / Legal Guardian' },
    { value: 'healthcare_agent', label: 'Healthcare Agent' },
];

const CONSENT_UPLOAD_MAX_BYTES = 15 * 1024 * 1024;

const Acknowledgement = ({ itemData, formData, onNext, onBack, patientId }) => {
    const dispatch = useDispatch();
    const [receivedNotice, setReceivedNotice] = useState('')
    const [haveCopy, setHaveCopy] = useState('')
    const [refusedReceipt, setRefusedReceipt] = useState('')
    let { getPatientDataRes } = useSelector(state => state.patientProfile);
    let { currentBranch } = useSelector(state => state.branch);
    let { userData } = useSelector(state => state.auth);
    let { getRes: consentGetRes, postRes: consentPostRes, uploadRes: consentUploadRes, loading: consentLoading } = useSelector(state => state.patientConsent);

    // --- General Consent (DWMS-FRM-007) state -------------------------
    const consent = consentGetRes?.patientConsent;
    const [redoConsent, setRedoConsent] = useState(false);
    const [consentMode, setConsentMode] = useState('sign'); // 'sign' | 'upload'
    const [signedByName, setSignedByName] = useState('');
    const [signedByRelationship, setSignedByRelationship] = useState('patient');
    const [patientSignature, setPatientSignature] = useState('');
    const [witnessName, setWitnessName] = useState('');
    const [witnessTitle, setWitnessTitle] = useState('');
    const [witnessSignature, setWitnessSignature] = useState('');
    const [interpreterUsed, setInterpreterUsed] = useState(false);
    const [interpreterName, setInterpreterName] = useState('');
    const [interpreterSignature, setInterpreterSignature] = useState('');
    const [consentFile, setConsentFile] = useState(null);
    // Bumping the epoch remounts the signature pads (wipes the canvases)
    // after a successful save.
    const [padEpoch, setPadEpoch] = useState(0);
    const consentFileInputRef = useRef(null);

    const { register, handleSubmit, formState: { errors }, setValue } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: formData,
    });

    // Prefer the explicit patientId prop (edit mode) over the pId left in
    // localStorage, which may be stale from an earlier add-patient session.
    const resolvedPId = itemData?.pId ?? (patientId || localStorage.getItem("pId"));

    useEffect(() => {
        const pId = localStorage.getItem("pId")
        if (itemData?.pId || pId || patientId) {
            dispatch(getPatientDataRequest({ type: "acknowledgement", id: itemData?.pId ?? (pId || patientId) }))
        }

    }, [dispatch, itemData?.pId, patientId])

    useEffect(() => {
        if (resolvedPId) {
            dispatch(getPatientConsentRequest({ pId: resolvedPId }));
        }
    }, [dispatch, resolvedPId]);

    // The witness is the DWMS employee performing the registration —
    // prefill from the logged-in account but leave the fields editable.
    useEffect(() => {
        if (userData?.name) setWitnessName((prev) => prev || userData.name);
        if (userData?.role) setWitnessTitle((prev) => prev || userData.role);
    }, [userData]);

    useEffect(() => {
        if (getPatientDataRes?.patientData) {
            if (getPatientDataRes?.patientData?.acknowledgement) {
                setReceivedNotice(getPatientDataRes?.patientData?.acknowledgement[0])
                setHaveCopy(getPatientDataRes?.patientData?.acknowledgement[0])
                setRefusedReceipt(getPatientDataRes?.patientData?.acknowledgement[0])
                setValue('acknowledgement', getPatientDataRequest?.patientData?.acknowledgement[0])
            }
        }
    }, [getPatientDataRes, setValue]);

    // Consent save/upload responses: toast, clear the capture fields and
    // re-fetch so the "consent on file" summary replaces the capture UI.
    useEffect(() => {
        const handleConsentResponse = (res, resetAction) => {
            if (!res) return;
            if (res.success) {
                toast.success(res.message);
                setRedoConsent(false);
                setSignedByName('');
                setSignedByRelationship('patient');
                setPatientSignature('');
                setWitnessSignature('');
                setInterpreterUsed(false);
                setInterpreterName('');
                setInterpreterSignature('');
                setConsentFile(null);
                if (consentFileInputRef.current) consentFileInputRef.current.value = '';
                setPadEpoch((n) => n + 1);
                if (resolvedPId) dispatch(getPatientConsentRequest({ pId: resolvedPId }));
            } else {
                const msg = res?.message || (typeof res === 'string' ? res : 'Could not save the consent. Please try again.');
                toast.error(msg);
            }
            dispatch(resetAction());
        };
        handleConsentResponse(consentPostRes, resetPostPatientConsent);
        handleConsentResponse(consentUploadRes, resetUploadPatientConsent);
    }, [consentPostRes, consentUploadRes, dispatch, resolvedPId]);

    const onSubmit = (data) => {
        const pId = localStorage.getItem("pId")
        if (getPatientDataRes?.patientData) {
            dispatch(putPatientAcknowledgementRequest({ formData: data, _id: itemData?.pId ?? (pId || patientId) }))
        }
        else {
            dispatch(postPatientAcknowledgementRequest({ formData: data.acknowledgement || [], branchId: currentBranch?.id, pId: patientId || pId }))
        }
        // No onNext() here: this is the final step. The wrapping
        // FormStepper watches the redux response and renders the success
        // card + navigates back when the save resolves.
    };

    const handleConsentFileSelect = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > CONSENT_UPLOAD_MAX_BYTES) {
            toast.error('File too large. Maximum size is 15 MB.');
            e.target.value = '';
            return;
        }
        setConsentFile(file);
    };

    const saveConsent = () => {
        if (!resolvedPId) {
            toast.error("Save the patient's basic information first.");
            return;
        }
        if (consentMode === 'upload') {
            if (!consentFile) {
                toast.error('Choose the scanned signed consent file first.');
                return;
            }
            dispatch(uploadPatientConsentRequest({ pId: resolvedPId, branchId: currentBranch?.id, file: consentFile }));
            return;
        }
        if (!signedByName.trim()) {
            toast.error('Enter the name of the person signing.');
            return;
        }
        if (!patientSignature) {
            toast.error('The patient/guardian signature is required.');
            return;
        }
        if (!witnessName.trim()) {
            toast.error('Enter the witness name.');
            return;
        }
        if (!witnessSignature) {
            toast.error('The witness signature is required.');
            return;
        }
        if (interpreterUsed && (!interpreterName.trim() || !interpreterSignature)) {
            toast.error("Enter the interpreter's name and signature, or untick the interpreter option.");
            return;
        }
        dispatch(postPatientConsentRequest({
            pId: resolvedPId,
            branchId: currentBranch?.id,
            signedByName: signedByName.trim(),
            signedByRelationship,
            patientSignature,
            witnessName: witnessName.trim(),
            witnessTitle: witnessTitle.trim(),
            witnessSignature,
            interpreterUsed,
            interpreterName: interpreterName.trim(),
            interpreterSignature,
        }));
    };

    const viewConsent = () => {
        if (consent?._id) {
            window.open(apiBuildUrl(`api/patient-consent-file/${consent._id}`), '_blank', 'noopener');
        }
    };

    const consentSummary = consent
        ? (consent.method === 'digital'
            ? `Signed on screen by ${consent.signedByName || 'patient'} on ${consent.signedAt ? new Date(consent.signedAt).toLocaleDateString() : '—'}, witnessed by ${consent.witnessName || '—'}.`
            : `Signed paper copy uploaded on ${consent.signedAt ? new Date(consent.signedAt).toLocaleDateString() : '—'}.`)
        : '';

    const handleChange = (fieldName) => () => {
        switch (fieldName) {
            case "receivedNotice":
                if (receivedNotice === "receivedNotice") {
                    setReceivedNotice("");
                } else {
                    setReceivedNotice("receivedNotice");
                }
                break;
            case "alreadyHaveCopy":
                if (haveCopy === "alreadyHaveCopy") {
                    setHaveCopy("");
                } else {
                    setHaveCopy("alreadyHaveCopy");
                }
                break;
            case "refusedReceipt":
                if (refusedReceipt === "refusedReceipt") {
                    setRefusedReceipt("");
                } else {
                    setRefusedReceipt("refusedReceipt");
                }
                break;
            default:
        }
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <Box>
                <Card className='cardStyle' sx={{ marginTop: "40px" }}>
                    <Typography className='title'>General Consent to Treatment (DWMS-FRM-007)</Typography>
                    {consent && !redoConsent ? (
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                gap: 2,
                                p: 2,
                                borderRadius: 1.5,
                                bgcolor: 'rgba(46,125,50,0.08)',
                                border: '1px solid rgba(46,125,50,0.25)',
                            }}
                        >
                            <CheckCircleRoundedIcon sx={{ color: 'success.main', fontSize: 32 }} />
                            <Box sx={{ flex: 1, minWidth: 220 }}>
                                <Typography sx={{ fontWeight: 600 }}>Consent on file</Typography>
                                <Typography variant="body2" color="text.secondary">{consentSummary}</Typography>
                            </Box>
                            <Button onClick={viewConsent} startIcon={<OpenInNewIcon />} variant="outlined" size="small">
                                View signed form
                            </Button>
                            <Button onClick={() => setRedoConsent(true)} size="small">
                                Replace
                            </Button>
                        </Box>
                    ) : (
                        <Box>
                            <ToggleButtonGroup
                                value={consentMode}
                                exclusive
                                onChange={(e, mode) => { if (mode !== null) setConsentMode(mode); }}
                                size="small"
                                sx={{ mb: 2 }}
                            >
                                <ToggleButton value="sign">
                                    <DrawIcon sx={{ mr: 0.5, fontSize: 18 }} /> Sign on screen
                                </ToggleButton>
                                <ToggleButton value="upload">
                                    <UploadFileIcon sx={{ mr: 0.5, fontSize: 18 }} /> Upload signed copy
                                </ToggleButton>
                            </ToggleButtonGroup>
                            {consent && redoConsent && (
                                <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
                                    Saving a new consent will replace the one currently on file.
                                    <Button size="small" onClick={() => setRedoConsent(false)} sx={{ ml: 1 }}>Cancel</Button>
                                </Typography>
                            )}

                            {consentMode === 'sign' ? (
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <Box
                                            sx={{
                                                p: 2,
                                                border: '1px solid rgba(0,0,0,0.15)',
                                                borderRadius: 1.5,
                                                bgcolor: '#FAFBFC',
                                                maxHeight: 200,
                                                overflowY: 'auto',
                                            }}
                                        >
                                            <ol style={{ margin: 0, paddingLeft: 20 }}>
                                                {CONSENT_CLAUSES.map((clause, i) => (
                                                    <li key={i}>
                                                        <Typography variant="body2" sx={{ mb: 1 }}>{clause}</Typography>
                                                    </li>
                                                ))}
                                            </ol>
                                            <Typography variant="body2">{CONSENT_CLOSING}</Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            label="Signed by (full name)"
                                            fullWidth
                                            size="small"
                                            value={signedByName}
                                            onChange={(e) => setSignedByName(e.target.value)}
                                            helperText="The patient, or the guardian/agent signing on their behalf"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            label="Signing as"
                                            select
                                            fullWidth
                                            size="small"
                                            value={signedByRelationship}
                                            onChange={(e) => setSignedByRelationship(e.target.value)}
                                        >
                                            {RELATIONSHIP_OPTIONS.map((opt) => (
                                                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <ConsentSignaturePad
                                            key={`patient-${padEpoch}`}
                                            label="Patient / Guardian / Agent signature"
                                            value={patientSignature}
                                            onChange={setPatientSignature}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Divider textAlign="left">
                                            <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.5 }}>WITNESS / DWMS EMPLOYEE</Typography>
                                        </Divider>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            label="Witness name"
                                            fullWidth
                                            size="small"
                                            value={witnessName}
                                            onChange={(e) => setWitnessName(e.target.value)}
                                            helperText="Staff member who is not the patient's physician or authorized provider"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            label="Witness title"
                                            fullWidth
                                            size="small"
                                            value={witnessTitle}
                                            onChange={(e) => setWitnessTitle(e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <ConsentSignaturePad
                                            key={`witness-${padEpoch}`}
                                            label="Witness signature"
                                            value={witnessSignature}
                                            onChange={setWitnessSignature}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={interpreterUsed}
                                                    onChange={(e) => setInterpreterUsed(e.target.checked)}
                                                />
                                            }
                                            label="An interpreter/translator assisted the patient"
                                        />
                                    </Grid>
                                    {interpreterUsed && (
                                        <>
                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    label="Interpreter/Translator name"
                                                    fullWidth
                                                    size="small"
                                                    value={interpreterName}
                                                    onChange={(e) => setInterpreterName(e.target.value)}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <ConsentSignaturePad
                                                    key={`interpreter-${padEpoch}`}
                                                    label="Interpreter/Translator signature"
                                                    value={interpreterSignature}
                                                    onChange={setInterpreterSignature}
                                                />
                                            </Grid>
                                        </>
                                    )}
                                    <Grid item xs={12}>
                                        <Button onClick={saveConsent} className='commonBtn' disabled={consentLoading}>
                                            Save signed consent
                                        </Button>
                                    </Grid>
                                </Grid>
                            ) : (
                                <Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                        Have the printed consent form signed, then upload the scanned copy (PDF, JPG or PNG — max 15 MB).
                                    </Typography>
                                    <input
                                        ref={consentFileInputRef}
                                        type="file"
                                        accept="application/pdf,image/png,image/jpeg"
                                        style={{ display: 'none' }}
                                        onChange={handleConsentFileSelect}
                                    />
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                                        <Button
                                            variant="outlined"
                                            startIcon={<UploadFileIcon />}
                                            onClick={() => consentFileInputRef.current?.click()}
                                        >
                                            Choose file
                                        </Button>
                                        {consentFile && (
                                            <Typography variant="body2">{consentFile.name}</Typography>
                                        )}
                                    </Box>
                                    <Box sx={{ mt: 2 }}>
                                        <Button onClick={saveConsent} className='commonBtn' disabled={consentLoading || !consentFile}>
                                            Upload signed consent
                                        </Button>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    )}
                </Card>

                <Card className='cardStyle' sx={{ marginTop: "40px" }}>
                    <Typography className='title'>Consent & Acknowledgements</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography>By checking one of the boxes below, I acknowledge :<span ariaHidden="true" className="MuiFormLabel-asterisk css-wgai2y-MuiFormLabel-asterisk">&thinsp;*</span></Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth error={Boolean(errors.acknowledgement)}>
                                <FormGroup sx={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                {...register('acknowledgement')}
                                                value="receivedNotice"
                                                checked={receivedNotice === "receivedNotice"}
                                                onChange={handleChange("receivedNotice")}
                                            />
                                        }
                                        label="Patient has signed Policy Documents"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                {...register('acknowledgement')}
                                                value="alreadyHaveCopy"
                                                checked={haveCopy === "alreadyHaveCopy"}
                                                onChange={handleChange("alreadyHaveCopy")}
                                            />
                                        }
                                        label="Patient has refused to sign Policy Documents"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                {...register('acknowledgement')}
                                                value="refusedReceipt"
                                                checked={refusedReceipt === "refusedReceipt"}
                                                onChange={handleChange("refusedReceipt")}
                                            />
                                        }
                                        label="Patient has not received Policy Documents"
                                    />
                                </FormGroup>
                                {errors?.acknowledgement?.message && <FormHelperText>{errors?.acknowledgement?.message}</FormHelperText>}
                            </FormControl>
                        </Grid>
                    </Grid>
                </Card>
            </Box>
            <Box display={'flex'} flexDirection={'row'} alignItems={'center'} justifyContent={'space-between'} marginTop={'40px'}>
                <Button onClick={onBack} className='commonBtn'>
                    Back
                </Button>
                <Box display={'flex'} flexDirection={'row'} alignItems={'left'} justifyContent={'space-between'} >
                    <Button type='submit' className='commonBtn' >
                        {getPatientDataRes?.patientData ? "Update and Exit" : "Save and Exit"}
                    </Button>
                </Box>
            </Box>
        </form>
    );
};

export default Acknowledgement;
